import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Pencil, Tag, X, Save, BarChart3, Copy, Check, ToggleLeft, ToggleRight, Search, Filter, ChevronLeft, ChevronRight, Download, Eye, Clock, User, Package, TrendingUp, TrendingDown, Minus, PieChart, Activity, Users, Percent, DollarSign, Calendar, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to format date for datetime-local input (avoiding timezone issues)
const formatDateTimeLocal = (dateString) => {
  if (!dateString) return '';
  // If it's already in YYYY-MM-DDTHH:mm format, return as is
  if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
    return dateString;
  }
  // Parse and format without timezone conversion
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const AdminDiscounts = () => {
  const { getAuthHeader } = useAdminAuth();
  const [discounts, setDiscounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [showBulkGenerator, setShowBulkGenerator] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  
  // Details view for selected discount
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [discountUsage, setDiscountUsage] = useState([]);
  const [loadingUsage, setLoadingUsage] = useState(false);
  
  // Filtering, sorting and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive, expired
  const [filterCategory, setFilterCategory] = useState('all'); // all or category name
  const [sortOrder, setSortOrder] = useState('newest'); // newest, oldest, name, code
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Categories extracted from discounts
  const [categories, setCategories] = useState([]);
  
  // FitNet data for product selection
  const [fitnetPasses, setFitnetPasses] = useState([]);
  const [fitnetSessions, setFitnetSessions] = useState([]);

  const emptyDiscount = {
    code: '',
    name: '',
    description: '',
    category: '', // NEW: category/group for organizing codes
    discountType: 'percentage',
    discountValue: 10,
    usageType: 'multiple',
    maxUsageTotal: null,
    maxUsagePerUser: null,
    validFrom: '',
    validTo: '',
    applicableTo: { type: 'all', passIds: [], sessionIds: [] },
    isActive: true
  };

  const emptyBulkConfig = {
    count: 10,
    prefix: '',
    codeLength: 8,
    name: 'Kod promocyjny',
    description: '',
    category: '', // NEW: category/group for bulk codes
    discountType: 'percentage',
    discountValue: 10,
    usageType: 'single',
    maxUsageTotal: 1,
    maxUsagePerUser: 1,
    validFrom: '',
    validTo: '',
    applicableTo: { type: 'all', passIds: [], sessionIds: [] }
  };

  const [bulkConfig, setBulkConfig] = useState(emptyBulkConfig);

  useEffect(() => {
    fetchDiscounts();
    fetchStats();
    fetchFitnetData();
  }, []);

  const fetchFitnetData = async () => {
    try {
      const [passesRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/api/admin/fitnet/passes`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/admin/fitnet/sessions`, { headers: getAuthHeader() })
      ]);
      setFitnetPasses(passesRes.data.passes || []);
      setFitnetSessions(sessionsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch FitNet data:', error);
    }
  };

  const fetchDiscounts = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/discounts`, {
        headers: getAuthHeader()
      });
      setDiscounts(response.data);
      
      // Extract unique categories from discounts
      const uniqueCategories = [...new Set(
        response.data
          .map(d => d.category)
          .filter(c => c && c.trim())
      )].sort();
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Failed to fetch discounts:', error);
      toast.error('Nie udało się pobrać danych');
    } finally {
      setLoading(false);
    }
  };

  // Fetch usage details for a specific discount
  const fetchDiscountUsage = async (code) => {
    setLoadingUsage(true);
    try {
      const response = await axios.get(`${API}/api/admin/discounts/${code}/usage`, {
        headers: getAuthHeader()
      });
      setDiscountUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch discount usage:', error);
      setDiscountUsage([]);
    } finally {
      setLoadingUsage(false);
    }
  };

  // Handle clicking on a discount to see details
  const handleViewDetails = (discount) => {
    setSelectedDiscount(discount);
    fetchDiscountUsage(discount.code);
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/discounts/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSave = async () => {
    if (!editingDiscount.code) {
      toast.error('Kod jest wymagany');
      return;
    }
    
    try {
      if (isCreating) {
        await axios.post(`${API}/api/admin/discounts`, editingDiscount, {
          headers: getAuthHeader()
        });
        toast.success('Kod rabatowy utworzony');
      } else {
        await axios.put(`${API}/api/admin/discounts/${editingDiscount.discount_id}`, editingDiscount, {
          headers: getAuthHeader()
        });
        toast.success('Kod rabatowy zaktualizowany');
      }
      setEditingDiscount(null);
      setIsCreating(false);
      fetchDiscounts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się zapisać');
    }
  };

  const handleDelete = async (discountId) => {
    if (!confirm('Czy na pewno chcesz usunąć ten kod?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/discounts/${discountId}`, {
        headers: getAuthHeader()
      });
      toast.success('Kod usunięty');
      fetchDiscounts();
      fetchStats();
    } catch (error) {
      toast.error('Nie udało się usunąć');
    }
  };

  const handleToggleActive = async (discountId, isActive) => {
    try {
      await axios.put(`${API}/api/admin/discounts/${discountId}`, { isActive }, {
        headers: getAuthHeader()
      });
      toast.success(isActive ? 'Kod rabatowy aktywowany' : 'Kod rabatowy dezaktywowany');
      fetchDiscounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się zmienić statusu');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'AVE';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setEditingDiscount({ ...editingDiscount, code });
  };

  const handleBulkGenerate = async () => {
    setBulkGenerating(true);
    setBulkResult(null);
    
    try {
      const response = await axios.post(`${API}/api/admin/discounts/bulk`, bulkConfig, {
        headers: getAuthHeader()
      });
      
      setBulkResult({
        success: true,
        message: response.data.message,
        codes: response.data.codes,
        batchId: response.data.batchId
      });
      
      toast.success(response.data.message);
      fetchDiscounts();
      fetchStats();
    } catch (error) {
      setBulkResult({
        success: false,
        message: error.response?.data?.error || 'Błąd generowania kodów'
      });
      toast.error(error.response?.data?.error || 'Błąd generowania kodów');
    } finally {
      setBulkGenerating(false);
    }
  };

  const copyAllCodes = () => {
    if (bulkResult?.codes) {
      navigator.clipboard.writeText(bulkResult.codes.join('\n'));
      toast.success('Skopiowano wszystkie kody');
    }
  };

  // Export codes to CSV for label printing
  const exportToCSV = (codesToExport = null) => {
    const codes = codesToExport || discounts;
    if (codes.length === 0) {
      toast.error('Brak kodów do eksportu');
      return;
    }

    // CSV headers
    const headers = ['Kod', 'Nazwa', 'Rabat', 'Typ', 'Ważny od', 'Ważny do', 'Status', 'Użyty'];
    
    // CSV rows
    const rows = codes.map(d => [
      d.code,
      d.name || '',
      d.discountType === 'percentage' ? `${d.discountValue}%` : `${d.discountValue} zł`,
      d.usageType === 'single' ? 'Jednorazowy' : 'Wielorazowy',
      d.validFrom ? new Date(d.validFrom).toLocaleDateString('pl-PL') : '',
      d.validTo ? new Date(d.validTo).toLocaleDateString('pl-PL') : '',
      d.isActive ? 'Aktywny' : 'Nieaktywny',
      d.currentUsageTotal || 0
    ]);

    // Build CSV content
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    // Add BOM for Excel Polish characters support
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `kody_rabatowe_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    URL.revokeObjectURL(url);
    toast.success(`Wyeksportowano ${codes.length} kodów`);
  };

  // Export only filtered codes
  const exportFilteredToCSV = () => {
    let filtered = discounts.filter(discount => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!discount.code.toLowerCase().includes(query) && 
            !(discount.name || '').toLowerCase().includes(query)) {
          return false;
        }
      }
      const isExpired = discount.validTo && new Date(discount.validTo) < new Date();
      if (filterStatus === 'active' && (!discount.isActive || isExpired)) return false;
      if (filterStatus === 'inactive' && discount.isActive) return false;
      if (filterStatus === 'expired' && !isExpired) return false;
      return true;
    });
    exportToCSV(filtered);
  };

  // Export for label printing (code only, one per line)
  const exportForLabels = () => {
    const codes = discounts.filter(d => d.isActive).map(d => d.code);
    if (codes.length === 0) {
      toast.error('Brak aktywnych kodów');
      return;
    }
    
    const content = codes.join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `etykiety_${new Date().toISOString().split('T')[0]}.txt`;
    link.click();
    
    URL.revokeObjectURL(url);
    toast.success(`Wyeksportowano ${codes.length} kodów dla etykiet`);
  };

  const getUsageStats = (code) => {
    if (!stats?.perCode) return null;
    return stats.perCode.find(s => s.discountCode === code);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Kody Rabatowe</h1>
          <p className="text-gray-400">Zarządzaj kodami promocyjnymi</p>
        </div>
        <div className="flex gap-3">
          {/* Export dropdown */}
          <div className="relative group">
            <Button
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            >
              <Download className="w-4 h-4 mr-2" />
              Eksport
            </Button>
            <div className="absolute right-0 mt-1 w-48 bg-gray-900 border border-white/10 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <button
                onClick={() => exportToCSV()}
                className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-white/5 hover:text-white rounded-t-lg"
              >
                Eksport CSV (wszystkie)
              </button>
              <button
                onClick={exportFilteredToCSV}
                className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-white/5 hover:text-white"
              >
                Eksport CSV (filtrowane)
              </button>
              <button
                onClick={exportForLabels}
                className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-white/5 hover:text-white rounded-b-lg"
              >
                Eksport dla etykiet (TXT)
              </button>
            </div>
          </div>
          <Button
            onClick={() => setShowBulkGenerator(true)}
            variant="outline"
            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
          >
            <Tag className="w-4 h-4 mr-2" />
            Generuj Hurtowo
          </Button>
          <Button
            onClick={() => { setEditingDiscount(emptyDiscount); setIsCreating(true); }}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nowy Kod
          </Button>
        </div>
      </div>

      {/* Bulk Generator Modal */}
      {showBulkGenerator && (
        <Card className="bg-zinc-900 border-purple-500/30">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Tag className="w-5 h-5 text-purple-400" />
                Generator kodów hurtowych
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => {setShowBulkGenerator(false); setBulkResult(null);}}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-white">Liczba kodów</Label>
                <Input
                  type="number"
                  min="1"
                  max="1000"
                  value={bulkConfig.count}
                  onChange={e => setBulkConfig({...bulkConfig, count: parseInt(e.target.value) || 1})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Prefix (opcjonalnie)</Label>
                <Input
                  placeholder="np. LATO"
                  value={bulkConfig.prefix}
                  onChange={e => setBulkConfig({...bulkConfig, prefix: e.target.value.toUpperCase()})}
                  className="bg-white/5 border-white/10 text-white uppercase"
                  maxLength={6}
                />
              </div>
              <div>
                <Label className="text-white">Długość kodu</Label>
                <Input
                  type="number"
                  min="4"
                  max="12"
                  value={bulkConfig.codeLength}
                  onChange={e => setBulkConfig({...bulkConfig, codeLength: parseInt(e.target.value) || 8})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Nazwa grupy</Label>
                <Input
                  value={bulkConfig.name}
                  onChange={e => setBulkConfig({...bulkConfig, name: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Kategoria (grupowanie)</Label>
                <Input
                  placeholder="np. Urodziny, Lato 2026, Influencerzy..."
                  value={bulkConfig.category || ''}
                  onChange={e => setBulkConfig({...bulkConfig, category: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  list="category-suggestions"
                />
                {categories.length > 0 && (
                  <datalist id="category-suggestions">
                    {categories.map(cat => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-white">Typ rabatu</Label>
                <Select
                  value={bulkConfig.discountType}
                  onValueChange={v => setBulkConfig({...bulkConfig, discountType: v})}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Procentowy (%)</SelectItem>
                    <SelectItem value="fixed">Kwotowy (zł)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white">Wartość rabatu</Label>
                <Input
                  type="number"
                  min="1"
                  value={bulkConfig.discountValue}
                  onChange={e => setBulkConfig({...bulkConfig, discountValue: parseFloat(e.target.value) || 0})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Ważny od</Label>
                <Input
                  type="date"
                  value={bulkConfig.validFrom}
                  onChange={e => setBulkConfig({...bulkConfig, validFrom: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Ważny do</Label>
                <Input
                  type="date"
                  value={bulkConfig.validTo}
                  onChange={e => setBulkConfig({...bulkConfig, validTo: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>

            {/* Product Selection */}
            <div className="space-y-3 pt-2">
              <Label className="text-white">Zastosowanie kodu</Label>
              <Select
                value={bulkConfig.applicableTo?.type || 'all'}
                onValueChange={v => setBulkConfig({...bulkConfig, applicableTo: { type: v, passIds: [], sessionIds: [] }})}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie produkty</SelectItem>
                  <SelectItem value="passes">Tylko karnety</SelectItem>
                  <SelectItem value="sessions">Tylko zajęcia</SelectItem>
                </SelectContent>
              </Select>
              
              {bulkConfig.applicableTo?.type === 'passes' && fitnetPasses.length > 0 && (
                <div className="bg-white/5 rounded-lg p-3 space-y-2 max-h-32 overflow-y-auto">
                  <p className="text-xs text-gray-400 mb-2">Wybierz karnety (opcjonalnie - puste = wszystkie):</p>
                  {fitnetPasses.map(pass => {
                    const passId = pass.variantId || pass.id;
                    return (
                    <label key={pass.id} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bulkConfig.applicableTo?.passIds?.includes(passId)}
                        onChange={(e) => {
                          const ids = bulkConfig.applicableTo?.passIds || [];
                          if (e.target.checked) {
                            setBulkConfig({...bulkConfig, applicableTo: { ...bulkConfig.applicableTo, passIds: [...ids, passId] }});
                          } else {
                            setBulkConfig({...bulkConfig, applicableTo: { ...bulkConfig.applicableTo, passIds: ids.filter(id => id !== passId) }});
                          }
                        }}
                        className="accent-purple-500"
                      />
                      {pass.name} <span className="text-gray-500 text-xs">(ID: {passId})</span> - {pass.price} zł
                    </label>
                  )})}
                </div>
              )}
              
              {bulkConfig.applicableTo?.type === 'sessions' && fitnetSessions.length > 0 && (
                <div className="bg-white/5 rounded-lg p-3 space-y-2 max-h-32 overflow-y-auto">
                  <p className="text-xs text-gray-400 mb-2">Wybierz typy zajęć (opcjonalnie - puste = wszystkie):</p>
                  {fitnetSessions.map(session => (
                    <label key={session.code} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bulkConfig.applicableTo?.sessionIds?.includes(session.code)}
                        onChange={(e) => {
                          const ids = bulkConfig.applicableTo?.sessionIds || [];
                          if (e.target.checked) {
                            setBulkConfig({...bulkConfig, applicableTo: { ...bulkConfig.applicableTo, sessionIds: [...ids, session.code] }});
                          } else {
                            setBulkConfig({...bulkConfig, applicableTo: { ...bulkConfig.applicableTo, sessionIds: ids.filter(id => id !== session.code) }});
                          }
                        }}
                        className="accent-purple-500"
                      />
                      {session.name} <span className="text-gray-500 text-xs">({session.code})</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <p className="text-sm text-gray-400">
                Przykład kodu: <span className="text-purple-400 font-mono">{bulkConfig.prefix}{'X'.repeat(bulkConfig.codeLength)}</span>
              </p>
              <Button
                onClick={handleBulkGenerate}
                disabled={bulkGenerating}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {bulkGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Generowanie...
                  </>
                ) : (
                  <>Wygeneruj {bulkConfig.count} kodów</>
                )}
              </Button>
            </div>

            {/* Bulk Result */}
            {bulkResult && (
              <div className={`p-4 rounded-lg ${bulkResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className={bulkResult.success ? 'text-green-400' : 'text-red-400'}>
                    {bulkResult.message}
                  </p>
                  {bulkResult.success && (
                    <Button variant="ghost" size="sm" onClick={copyAllCodes}>
                      <Copy className="w-4 h-4 mr-1" />
                      Kopiuj wszystkie
                    </Button>
                  )}
                </div>
                {bulkResult.codes && (
                  <div className="max-h-32 overflow-y-auto bg-black/20 rounded p-2 font-mono text-sm text-gray-300">
                    {bulkResult.codes.slice(0, 20).join(', ')}
                    {bulkResult.codes.length > 20 && ` ... i ${bulkResult.codes.length - 20} więcej`}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats Dashboard */}
      {stats && (
        <div className="space-y-6">
          {/* KPI Cards Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Total Usage */}
            <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider">Użyć łącznie</p>
                    <p className="text-3xl font-bold text-white mt-1">
                      {stats.summary?.allTime?.totalUsed || stats.perCode?.reduce((sum, s) => sum + s.totalUsed, 0) || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <Activity className="w-6 h-6 text-purple-400" />
                  </div>
                </div>
                {stats.summary?.last30Days && (
                  <div className="flex items-center mt-3 text-sm">
                    {stats.summary.last30Days.usageTrend > 0 ? (
                      <TrendingUp className="w-4 h-4 text-green-400 mr-1" />
                    ) : stats.summary.last30Days.usageTrend < 0 ? (
                      <TrendingDown className="w-4 h-4 text-red-400 mr-1" />
                    ) : (
                      <Minus className="w-4 h-4 text-gray-400 mr-1" />
                    )}
                    <span className={stats.summary.last30Days.usageTrend > 0 ? 'text-green-400' : stats.summary.last30Days.usageTrend < 0 ? 'text-red-400' : 'text-gray-400'}>
                      {stats.summary.last30Days.usageTrend > 0 ? '+' : ''}{stats.summary.last30Days.usageTrend}%
                    </span>
                    <span className="text-gray-500 ml-1">vs poprz. 30 dni</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Total Discount Given */}
            <Card className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 border-orange-500/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider">Suma rabatów</p>
                    <p className="text-3xl font-bold text-white mt-1">
                      {(stats.summary?.allTime?.totalDiscount || stats.perCode?.reduce((sum, s) => sum + s.totalDiscount, 0) || 0).toFixed(0)} <span className="text-lg text-gray-400">zł</span>
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-orange-400" />
                  </div>
                </div>
                {stats.summary?.last30Days && (
                  <div className="flex items-center mt-3 text-sm">
                    {stats.summary.last30Days.discountTrend > 0 ? (
                      <TrendingUp className="w-4 h-4 text-green-400 mr-1" />
                    ) : stats.summary.last30Days.discountTrend < 0 ? (
                      <TrendingDown className="w-4 h-4 text-red-400 mr-1" />
                    ) : (
                      <Minus className="w-4 h-4 text-gray-400 mr-1" />
                    )}
                    <span className={stats.summary.last30Days.discountTrend > 0 ? 'text-green-400' : stats.summary.last30Days.discountTrend < 0 ? 'text-red-400' : 'text-gray-400'}>
                      {stats.summary.last30Days.discountTrend > 0 ? '+' : ''}{stats.summary.last30Days.discountTrend}%
                    </span>
                    <span className="text-gray-500 ml-1">vs poprz. 30 dni</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Unique Users */}
            <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider">Unikalni użytkownicy</p>
                    <p className="text-3xl font-bold text-white mt-1">
                      {stats.summary?.allTime?.uniqueUsers || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                </div>
                <div className="flex items-center mt-3 text-sm text-gray-500">
                  <Calendar className="w-4 h-4 mr-1" />
                  {stats.summary?.last30Days?.uniqueUsers || 0} w ostatnich 30 dniach
                </div>
              </CardContent>
            </Card>

            {/* Average Discount */}
            <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider">Śr. rabat</p>
                    <p className="text-3xl font-bold text-white mt-1">
                      {stats.summary?.allTime?.avgDiscount || 0} <span className="text-lg text-gray-400">zł</span>
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <Percent className="w-6 h-6 text-green-400" />
                  </div>
                </div>
                <div className="flex items-center mt-3 text-sm text-gray-500">
                  <Activity className="w-4 h-4 mr-1" />
                  {stats.summary?.last7Days?.totalUsed || 0} użyć w tym tygodniu
                  {stats.summary?.last7Days?.weeklyTrend !== undefined && (
                    <span className={`ml-2 ${stats.summary.last7Days.weeklyTrend > 0 ? 'text-green-400' : stats.summary.last7Days.weeklyTrend < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                      ({stats.summary.last7Days.weeklyTrend > 0 ? '+' : ''}{stats.summary.last7Days.weeklyTrend}%)
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Usage Timeline Chart */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                  Użycia w czasie (ostatnie 30 dni)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.timeline && stats.timeline.length > 0 ? (
                  <div className="h-48">
                    {/* Simple bar chart visualization */}
                    <div className="flex items-end justify-between h-40 gap-1">
                      {(() => {
                        const maxCount = Math.max(...stats.timeline.map(t => t.count), 1);
                        // Show last 14 days for better visualization
                        const displayData = stats.timeline.slice(-14);
                        return displayData.map((item, idx) => {
                          const height = (item.count / maxCount) * 100;
                          const date = new Date(item._id);
                          const dayName = date.toLocaleDateString('pl-PL', { weekday: 'short' }).slice(0, 2);
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center group">
                              <div className="relative w-full">
                                <div 
                                  className="w-full bg-gradient-to-t from-purple-600 to-purple-400 rounded-t transition-all group-hover:from-purple-500 group-hover:to-purple-300"
                                  style={{ height: `${Math.max(height, 4)}%`, minHeight: '4px' }}
                                />
                                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  {item.count} użyć • {item.totalDiscount?.toFixed(0) || 0} zł
                                </div>
                              </div>
                              <span className="text-[10px] text-gray-500 mt-1">{dayName}</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-gray-500">
                    Brak danych do wyświetlenia
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Category Distribution */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-orange-400" />
                  Użycia wg kategorii
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.byCategory && stats.byCategory.length > 0 ? (
                  <div className="space-y-3">
                    {(() => {
                      const total = stats.byCategory.reduce((sum, c) => sum + c.count, 0);
                      const colors = ['bg-purple-500', 'bg-orange-500', 'bg-blue-500', 'bg-green-500', 'bg-pink-500', 'bg-yellow-500'];
                      return stats.byCategory.slice(0, 6).map((cat, idx) => {
                        const percent = total > 0 ? ((cat.count / total) * 100).toFixed(1) : 0;
                        return (
                          <div key={idx} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-300">{cat.category || 'Bez kategorii'}</span>
                              <span className="text-gray-400">{cat.count} ({percent}%)</span>
                            </div>
                            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${colors[idx % colors.length]} rounded-full transition-all`}
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-gray-500">
                    Brak danych do wyświetlenia
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Usage Table */}
          {stats.recentUsage && stats.recentUsage.length > 0 && (
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-400" />
                  Ostatnie użycia kodów
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-gray-400 text-sm border-b border-white/10">
                        <th className="pb-3 font-medium">Użytkownik</th>
                        <th className="pb-3 font-medium">Kod</th>
                        <th className="pb-3 font-medium text-right">Rabat</th>
                        <th className="pb-3 font-medium text-right">Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentUsage.map((usage, idx) => (
                        <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-blue-400" />
                              </div>
                              <div>
                                <p className="text-white text-sm">{usage.userName}</p>
                                {usage.userEmail && (
                                  <p className="text-gray-500 text-xs">{usage.userEmail}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3">
                            <button 
                              onClick={() => copyCode(usage.code)}
                              className="font-mono text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1 text-sm"
                            >
                              {usage.code}
                              <Copy className="w-3 h-3 opacity-50" />
                            </button>
                          </td>
                          <td className="py-3 text-right">
                            <span className="text-orange-400 font-medium">{usage.discountAmount?.toFixed(0) || 0} zł</span>
                          </td>
                          <td className="py-3 text-right text-gray-400 text-sm">
                            {usage.usedAt ? new Date(usage.usedAt).toLocaleDateString('pl-PL', { 
                              day: 'numeric', 
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Stats - Existing Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-purple-400">{discounts.length}</p>
                <p className="text-gray-400 text-sm">Wszystkie kody</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-400">
                  {discounts.filter(d => d.isActive).length}
                </p>
                <p className="text-gray-400 text-sm">Aktywne</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-red-400">
                  {discounts.filter(d => !d.isActive).length}
                </p>
                <p className="text-gray-400 text-sm">Nieaktywne</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-yellow-400">
                  {discounts.filter(d => d.validTo && new Date(d.validTo) < new Date()).length}
                </p>
                <p className="text-gray-400 text-sm">Wygasłe</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mt-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Szukaj po kodzie lub nazwie..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="pl-10 bg-white/5 border-white/10 text-white"
          />
        </div>
        <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setCurrentPage(1); }}>
          <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie</SelectItem>
            <SelectItem value="active">Aktywne</SelectItem>
            <SelectItem value="inactive">Nieaktywne</SelectItem>
            <SelectItem value="expired">Wygasłe</SelectItem>
          </SelectContent>
        </Select>
        {categories.length > 0 && (
          <Select value={filterCategory} onValueChange={(v) => { setFilterCategory(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-[200px] bg-white/5 border-white/10 text-white">
              <Tag className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Kategoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie kategorie</SelectItem>
              <SelectItem value="_uncategorized">Bez kategorii</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {/* Sorting */}
        <Select value={sortOrder} onValueChange={(v) => { setSortOrder(v); setCurrentPage(1); }}>
          <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Sortowanie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Od najnowszych</SelectItem>
            <SelectItem value="oldest">Od najstarszych</SelectItem>
            <SelectItem value="name">Wg nazwy A-Z</SelectItem>
            <SelectItem value="code">Wg kodu A-Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Discounts List */}
      <div className="space-y-3">
        {(() => {
          // Filter discounts
          let filtered = discounts.filter(discount => {
            // Search filter
            if (searchQuery) {
              const query = searchQuery.toLowerCase();
              if (!discount.code.toLowerCase().includes(query) && 
                  !(discount.name || '').toLowerCase().includes(query)) {
                return false;
              }
            }
            // Status filter
            const isExpired = discount.validTo && new Date(discount.validTo) < new Date();
            if (filterStatus === 'active' && (!discount.isActive || isExpired)) return false;
            if (filterStatus === 'inactive' && discount.isActive) return false;
            if (filterStatus === 'expired' && !isExpired) return false;
            // Category filter
            if (filterCategory !== 'all') {
              if (filterCategory === '_uncategorized') {
                if (discount.category && discount.category.trim()) return false;
              } else {
                if (discount.category !== filterCategory) return false;
              }
            }
            return true;
          });
          
          // Sort discounts
          filtered.sort((a, b) => {
            switch (sortOrder) {
              case 'oldest':
                return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
              case 'name':
                return (a.name || '').localeCompare(b.name || '', 'pl');
              case 'code':
                return (a.code || '').localeCompare(b.code || '', 'pl');
              case 'newest':
              default:
                return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
            }
          });
          
          // Pagination
          const totalPages = Math.ceil(filtered.length / itemsPerPage);
          const startIndex = (currentPage - 1) * itemsPerPage;
          const paginatedDiscounts = filtered.slice(startIndex, startIndex + itemsPerPage);
          
          // Helper function to display applicable items
          const getApplicableText = (discount) => {
            const app = discount.applicableTo;
            if (!app || app.type === 'all') return null;
            
            if (app.type === 'passes' || app.type === 'selected_passes') {
              const passIds = app.passIds || [];
              if (passIds.length === 0) return app.type === 'passes' ? 'Wszystkie karnety' : 'Wybrane karnety';
              // Try to get pass names from fitnetPasses
              const passNames = passIds.map(id => {
                const idNum = parseInt(id);
                const idStr = String(id);
                const pass = fitnetPasses.find(p => 
                  p.variantId === idNum || p.variantId === idStr ||
                  p.id === idNum || p.id === idStr ||
                  p.code_name === idStr
                );
                return pass?.name || `Karnet #${id}`;
              });
              return passNames.length <= 2 ? passNames.join(', ') : `${passNames.slice(0, 2).join(', ')} +${passNames.length - 2}`;
            }
            
            if (app.type === 'sessions' || app.type === 'selected_sessions') {
              const sessionIds = app.sessionIds || app.sessionCodes || [];
              if (sessionIds.length === 0) return app.type === 'sessions' ? 'Wszystkie sesje' : 'Wybrane sesje';
              // Try to get session names from fitnetSessions
              const sessionNames = sessionIds.map(id => {
                const session = fitnetSessions.find(s => s.code === id);
                return session?.name || String(id).replace(/_/g, ' ');
              });
              return sessionNames.length <= 2 ? sessionNames.join(', ') : `${sessionNames.slice(0, 2).join(', ')} +${sessionNames.length - 2}`;
            }
            
            return null;
          };
          
          return (
            <>
              {/* Results info */}
              <div className="text-sm text-gray-400 mb-2">
                Wyświetlanie {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filtered.length)} z {filtered.length} kodów
              </div>
              
              {paginatedDiscounts.map((discount) => {
                const usage = getUsageStats(discount.code);
                const isExpired = discount.validTo && new Date(discount.validTo) < new Date();
                const isNotYetValid = discount.validFrom && new Date(discount.validFrom) > new Date();
          
                return (
                  <Card key={discount.discount_id} className="mb-4 bg-white/5 border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Tag className="w-6 h-6 text-orange-400" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button 
                              onClick={() => copyCode(discount.code)}
                              className="flex items-center gap-1 text-white font-mono font-bold hover:text-purple-400 transition-colors"
                            >
                              {discount.code}
                              {copiedCode === discount.code ? (
                                <Check className="w-4 h-4 text-green-400" />
                              ) : (
                                <Copy className="w-3 h-3 opacity-50" />
                              )}
                            </button>
                            
                            {/* USED FLAG - prominent green badge */}
                            {(usage?.totalUsed > 0 || discount.currentUsageTotal > 0) && (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full font-medium flex items-center gap-1">
                                <Check className="w-3 h-3" />
                                UŻYTY {usage?.totalUsed || discount.currentUsageTotal}x
                              </span>
                            )}
                            
                            {!discount.isActive && (
                              <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">Nieaktywny</span>
                            )}
                            {isExpired && (
                              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">Wygasł</span>
                            )}
                            {isNotYetValid && (
                              <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded">Jeszcze nieaktywny</span>
                            )}
                            {discount.category && (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">{discount.category}</span>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm">{discount.name || 'Bez nazwy'}</p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                            <span>
                              {discount.discountType === 'percentage' 
                                ? `${discount.discountValue}%` 
                                : `${discount.discountValue} zł`}
                            </span>
                            <span>
                              {discount.usageType === 'single' ? 'Jednorazowy' : 'Wielorazowy'}
                            </span>
                            {discount.maxUsageTotal && (
                              <span>Limit: {discount.currentUsageTotal || 0}/{discount.maxUsageTotal}</span>
                            )}
                            {/* Applicable products */}
                            {getApplicableText(discount) && (
                              <span className="text-cyan-400 flex items-center gap-1">
                                <Package className="w-3 h-3" />
                                {getApplicableText(discount)}
                              </span>
                            )}
                            {/* Validity period OD - DO */}
                            {(discount.validFrom || discount.validTo) && (
                              <span className="text-purple-400">
                                {discount.validFrom && discount.validTo ? (
                                  <>
                                    {new Date(discount.validFrom).toLocaleDateString('pl-PL')} - {new Date(discount.validTo).toLocaleDateString('pl-PL')}
                                  </>
                                ) : discount.validFrom ? (
                                  <>od {new Date(discount.validFrom).toLocaleDateString('pl-PL')}</>
                                ) : (
                                  <>do {new Date(discount.validTo).toLocaleDateString('pl-PL')}</>
                                )}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewDetails(discount)}
                            className="text-gray-400 hover:text-blue-400"
                            title="Szczegóły"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleActive(discount.discount_id, !discount.isActive)}
                            className={discount.isActive ? "text-green-400 hover:text-green-300" : "text-gray-400 hover:text-green-400"}
                            title={discount.isActive ? 'Dezaktywuj' : 'Aktywuj'}
                          >
                            {discount.isActive ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                          </Button>
                          {/* Delete button disabled - codes should not be deleted */}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="border-white/10"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-400">
                    Strona {currentPage} z {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="border-white/10"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              {filtered.length === 0 && (
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-8 text-center">
                    <Tag className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                    <p className="text-gray-400">
                      {searchQuery || filterStatus !== 'all' 
                        ? 'Brak kodów spełniających kryteria' 
                        : 'Brak kodów rabatowych'}
                    </p>
                    {!searchQuery && filterStatus === 'all' && (
                      <Button
                        onClick={() => { setEditingDiscount(emptyDiscount); setIsCreating(true); }}
                        variant="outline"
                        className="mt-4"
                      >
                        Utwórz pierwszy kod
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          );
        })()}
      </div>

      {/* Edit Modal */}
      {editingDiscount && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-gray-900 z-10">
              <CardTitle className="text-white">
                {isCreating ? 'Nowy Kod Rabatowy' : 'Edytuj Kod'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setEditingDiscount(null); setIsCreating(false); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Code */}
              <div className="space-y-2">
                <Label className="text-gray-300">Kod rabatowy</Label>
                <div className="flex gap-2">
                  <Input
                    value={editingDiscount.code || ''}
                    onChange={(e) => setEditingDiscount({ ...editingDiscount, code: e.target.value.toUpperCase() })}
                    placeholder="np. SUMMER2024"
                    className="bg-white/5 border-white/10 text-white font-mono uppercase"
                    disabled={!isCreating}
                  />
                  {isCreating && (
                    <Button variant="outline" onClick={generateCode}>
                      Generuj
                    </Button>
                  )}
                </div>
              </div>

              {/* Name & Description */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Nazwa</Label>
                  <Input
                    value={editingDiscount.name || ''}
                    onChange={(e) => setEditingDiscount({ ...editingDiscount, name: e.target.value })}
                    placeholder="np. Promocja letnia"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Kategoria (grupowanie)</Label>
                  <Input
                    value={editingDiscount.category || ''}
                    onChange={(e) => setEditingDiscount({ ...editingDiscount, category: e.target.value })}
                    placeholder="np. Urodziny, Lato 2026..."
                    className="bg-white/5 border-white/10 text-white"
                    list="edit-category-suggestions"
                  />
                  {categories.length > 0 && (
                    <datalist id="edit-category-suggestions">
                      {categories.map(cat => (
                        <option key={cat} value={cat} />
                      ))}
                    </datalist>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-gray-300">Opis</Label>
                <Input
                  value={editingDiscount.description || ''}
                  onChange={(e) => setEditingDiscount({ ...editingDiscount, description: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Discount Type & Value */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Typ rabatu</Label>
                  <Select
                    value={editingDiscount.discountType || 'percentage'}
                    onValueChange={(value) => setEditingDiscount({ ...editingDiscount, discountType: value })}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Procentowy (%)</SelectItem>
                      <SelectItem value="fixed">Kwotowy (zł)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">
                    Wartość ({editingDiscount.discountType === 'percentage' ? '%' : 'zł'})
                  </Label>
                  <Input
                    type="number"
                    value={editingDiscount.discountValue || 0}
                    onChange={(e) => setEditingDiscount({ ...editingDiscount, discountValue: parseFloat(e.target.value) })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Usage Type */}
              <div className="space-y-2">
                <Label className="text-gray-300">Typ użycia</Label>
                <Select
                  value={editingDiscount.usageType || 'multiple'}
                  onValueChange={(value) => setEditingDiscount({ ...editingDiscount, usageType: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Jednorazowy (1 użycie na kod)</SelectItem>
                    <SelectItem value="multiple">Wielorazowy</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Limits */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Limit użyć (łącznie)</Label>
                  <Input
                    type="number"
                    value={editingDiscount.maxUsageTotal || ''}
                    onChange={(e) => setEditingDiscount({ 
                      ...editingDiscount, 
                      maxUsageTotal: e.target.value ? parseInt(e.target.value) : null 
                    })}
                    placeholder="Bez limitu"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Limit na użytkownika</Label>
                  <Input
                    type="number"
                    value={editingDiscount.maxUsagePerUser || ''}
                    onChange={(e) => setEditingDiscount({ 
                      ...editingDiscount, 
                      maxUsagePerUser: e.target.value ? parseInt(e.target.value) : null 
                    })}
                    placeholder="Bez limitu"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Validity Period */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Ważny od</Label>
                  <Input
                    type="datetime-local"
                    value={formatDateTimeLocal(editingDiscount.validFrom)}
                    onChange={(e) => setEditingDiscount({ ...editingDiscount, validFrom: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Ważny do</Label>
                  <Input
                    type="datetime-local"
                    value={formatDateTimeLocal(editingDiscount.validTo)}
                    onChange={(e) => setEditingDiscount({ ...editingDiscount, validTo: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Applicable To */}
              <div className="space-y-3">
                <Label className="text-gray-300">Dotyczy produktów</Label>
                <Select
                  value={editingDiscount.applicableTo?.type || 'all'}
                  onValueChange={(value) => setEditingDiscount({ 
                    ...editingDiscount, 
                    applicableTo: { type: value, passIds: [], sessionIds: [] } 
                  })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Wszystkie produkty</SelectItem>
                    <SelectItem value="passes">Tylko wybrane karnety</SelectItem>
                    <SelectItem value="sessions">Tylko wybrane zajęcia</SelectItem>
                  </SelectContent>
                </Select>

                {editingDiscount.applicableTo?.type === 'passes' && fitnetPasses.length > 0 && (
                  <div className="p-3 bg-white/5 rounded-lg space-y-2 max-h-48 overflow-y-auto">
                    <p className="text-gray-400 text-sm">Wybierz karnety:</p>
                    {fitnetPasses.map(pass => {
                      // Use variantId (numeric) as the unique identifier - matches payment validation
                      const passId = pass.variantId || pass.id;
                      return (
                        <label key={passId} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(editingDiscount.applicableTo?.passIds || []).includes(passId)}
                            onChange={(e) => {
                              const current = editingDiscount.applicableTo?.passIds || [];
                              const updated = e.target.checked 
                                ? [...current, passId]
                                : current.filter(x => x !== passId);
                              setEditingDiscount({
                                ...editingDiscount,
                                applicableTo: { ...editingDiscount.applicableTo, passIds: updated }
                              });
                            }}
                            className="w-4 h-4 rounded accent-purple-500"
                          />
                          <span className="text-white">{pass.name}</span>
                          <span className="text-gray-500 text-xs">(ID: {passId})</span>
                          <span className="text-gray-500 text-xs">- {pass.price} zł</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {editingDiscount.applicableTo?.type === 'sessions' && fitnetSessions.length > 0 && (
                  <div className="p-3 bg-white/5 rounded-lg space-y-2 max-h-48 overflow-y-auto">
                    <p className="text-gray-400 text-sm">Wybierz zajęcia:</p>
                    {fitnetSessions.map(session => {
                      // Use code_name/code as the unique identifier - matches booking flow
                      const sessionId = session.code_name || session.code || session.id;
                      return (
                        <label key={sessionId} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(editingDiscount.applicableTo?.sessionIds || []).includes(sessionId)}
                            onChange={(e) => {
                              const current = editingDiscount.applicableTo?.sessionIds || [];
                              const updated = e.target.checked 
                                ? [...current, sessionId]
                                : current.filter(x => x !== sessionId);
                              setEditingDiscount({
                                ...editingDiscount,
                                applicableTo: { ...editingDiscount.applicableTo, sessionIds: updated }
                              });
                            }}
                            className="w-4 h-4 rounded accent-purple-500"
                          />
                          <span className="text-white">{session.name}</span>
                          <span className="text-gray-500 text-xs">({sessionId})</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Active */}
              <div className="flex items-center gap-3">
                <Switch
                  checked={editingDiscount.isActive}
                  onCheckedChange={(checked) => setEditingDiscount({ ...editingDiscount, isActive: checked })}
                />
                <Label className="text-gray-300">Aktywny</Label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setEditingDiscount(null); setIsCreating(false); }}>
                  Anuluj
                </Button>
                <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" />
                  Zapisz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Discount Details Modal */}
      {selectedDiscount && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl bg-gray-900/95 border-white/10 max-h-[80vh] overflow-hidden flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between border-b border-white/10">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <Tag className="w-5 h-5 text-purple-400" />
                  Szczegóły kodu: {selectedDiscount.code}
                </CardTitle>
                <p className="text-gray-400 text-sm mt-1">{selectedDiscount.name || 'Bez nazwy'}</p>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => { setSelectedDiscount(null); setDiscountUsage([]); }}
              >
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="p-6 overflow-y-auto space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-gray-400 text-xs mb-1">Typ rabatu</p>
                  <p className="text-white font-medium">
                    {selectedDiscount.discountType === 'percentage' 
                      ? `${selectedDiscount.discountValue}%` 
                      : `${selectedDiscount.discountValue} zł`}
                  </p>
                </div>
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-gray-400 text-xs mb-1">Wykorzystanie</p>
                  <p className="text-white font-medium">
                    {selectedDiscount.currentUsageTotal || selectedDiscount.usageCount || 0}
                    {selectedDiscount.maxUsageTotal ? ` / ${selectedDiscount.maxUsageTotal}` : ' (bez limitu)'}
                  </p>
                </div>
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-gray-400 text-xs mb-1">Ważność od</p>
                  <p className="text-white font-medium">
                    {selectedDiscount.validFrom 
                      ? new Date(selectedDiscount.validFrom).toLocaleString('pl-PL')
                      : 'Brak ograniczenia'}
                  </p>
                </div>
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-gray-400 text-xs mb-1">Ważność do</p>
                  <p className="text-white font-medium">
                    {selectedDiscount.validTo 
                      ? new Date(selectedDiscount.validTo).toLocaleString('pl-PL')
                      : 'Brak ograniczenia'}
                  </p>
                </div>
              </div>

              {/* Applicable Products */}
              <div className="p-4 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-purple-400" />
                  <p className="text-gray-300 font-medium">Przypisane produkty</p>
                </div>
                {(!selectedDiscount.applicableTo || selectedDiscount.applicableTo?.type === 'all') && (
                  <p className="text-gray-400">Wszystkie produkty</p>
                )}
                {(selectedDiscount.applicableTo?.type === 'passes' || selectedDiscount.applicableTo?.type === 'selected_passes') && (
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Karnety:</p>
                    <div className="flex flex-wrap gap-2">
                      {(selectedDiscount.applicableTo.passIds || []).map((passId, idx) => {
                        const passIdNum = parseInt(passId);
                        const passIdStr = String(passId);
                        const pass = fitnetPasses.find(p => 
                          p.variantId === passIdNum || p.variantId === passIdStr ||
                          p.id === passIdNum || p.id === passIdStr ||
                          p.code_name === passIdStr
                        );
                        return (
                          <span key={idx} className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded">
                            {pass?.name || `Karnet #${passId}`} <span className="text-purple-500 text-[10px]">({passId})</span>
                          </span>
                        );
                      })}
                      {(selectedDiscount.applicableTo.passIds || []).length === 0 && (
                        <span className="text-gray-500 text-sm">Brak przypisanych karnetów</span>
                      )}
                    </div>
                  </div>
                )}
                {(selectedDiscount.applicableTo?.type === 'sessions' || selectedDiscount.applicableTo?.type === 'selected_sessions') && (
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Zajęcia:</p>
                    <div className="flex flex-wrap gap-2">
                      {(selectedDiscount.applicableTo.sessionIds || selectedDiscount.applicableTo.sessionCodes || []).map((sessionId, idx) => {
                        const session = fitnetSessions.find(s => s.code === sessionId);
                        return (
                          <span key={idx} className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded">
                            {session?.name || sessionId.replace(/_/g, ' ')} <span className="text-blue-500 text-[10px]">({sessionId})</span>
                          </span>
                        );
                      })}
                      {(selectedDiscount.applicableTo.sessionIds || selectedDiscount.applicableTo.sessionCodes || []).length === 0 && (
                        <span className="text-gray-500 text-sm">Brak przypisanych zajęć</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Usage History */}
              <div className="p-4 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-green-400" />
                  <p className="text-gray-300 font-medium">Historia wykorzystania</p>
                </div>
                
                {loadingUsage ? (
                  <p className="text-gray-500 text-sm">Ładowanie...</p>
                ) : discountUsage.length === 0 ? (
                  <p className="text-gray-500 text-sm">Kod nie został jeszcze wykorzystany</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {discountUsage.map((usage, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-black/20 rounded text-sm">
                        <div className="flex items-center gap-3">
                          <User className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="text-white">{usage.userName || 'Nieznany użytkownik'}</p>
                            {usage.forUserName && (
                              <p className="text-yellow-400 text-xs">→ dla: {usage.forUserName}</p>
                            )}
                            {usage.bookingId && (
                              <p className="text-gray-500 text-xs">Rezerwacja: {usage.bookingId}</p>
                            )}
                            {usage.purchaseId && (
                              <p className="text-gray-500 text-xs">Zakup: {usage.purchaseId}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-400">
                            {usage.usedAt ? new Date(usage.usedAt).toLocaleString('pl-PL') : 'Brak daty'}
                          </p>
                          {usage.discountAmount && (
                            <p className="text-green-400 text-xs">-{usage.discountAmount} zł</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDiscounts;
